// @flow
import 'react-resizable/css/styles.css'

import { captureException } from '@sentry/react'
import API from 'api'
import Loading from 'components/Loading'
import ModalFormUnavailable from 'components/Modal/ModalFormUnavailable'
import LayoutWithSidebar from 'pages/Components/LayoutWithSidebar'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import BuilderDescriptionQuestionModal from 'pages/FormBuilder/Components/BuilderDescriptionQuestionModal'
import * as React from 'react'
import { useHistory } from 'react-router-dom'

import { ZentakeContext } from '@/utils/ZentakeContext'

import ConditionalRulesModalDialog from './ConditionalRulesModalDialog'
import FormBuilderSideMenu from './FormBuilderSideMenu'

type Props = {
  blockLoading: boolean,
  children: React.Node,
  editFormId: ?string,
  editMode: boolean,
}

function FormBuilderLayout({
  blockLoading,
  children,
  editFormId,
  editMode,
}: Props): React.Node {
  const { teamDetails } = React.useContext(ZentakeContext)
  const measuresEnabled = teamDetails.measures

  const history = useHistory()

  const notIsSettings = !history.location.pathname.includes('settings')
  const notIsDesign = !history.location.pathname.includes('design')
  const [fetchError, setFetchError] = React.useState(false)

  const {
    isEditingDescription,
    buildFromResponse,
    isFetchingForm,
    setIsFetchingForm,
    isSavingForm,
    getOnPreviewForm,
    onSaveForm,
    measuresEnabledForm,
  } = React.useContext(FormBuilderContext)

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setIsFetchingForm(true)
        const response = await API.getFormDetails(editFormId)
        if (response.detail === 'Not found.') {
          setFetchError(true)
        } else {
          buildFromResponse(response)
        }
      } catch (error) {
        captureException(error)
        setFetchError(true)
      } finally {
        setIsFetchingForm(false)
      }
    }
    if (editMode) {
      fetchData()
    }
  }, [editMode, editFormId, buildFromResponse, setIsFetchingForm])

  if (isFetchingForm && !blockLoading) {
    return <Loading />
  }

  if (fetchError)
    return (
      <ModalFormUnavailable
        hideButton
        isVisible
        description="Sorry, this form is not available"
        title="Form not available"
      />
    )

  return (
    <>
      <LayoutWithSidebar
        handlePreview={getOnPreviewForm?.(editFormId)}
        handleSave={onSaveForm}
        previewDisabled={!Boolean(editFormId)}
        saveLoading={isSavingForm || isFetchingForm}
        sideMenuContent={<FormBuilderSideMenu />}
        title="Forms/Zentake"
      >
        {children}
      </LayoutWithSidebar>
      {isEditingDescription && <BuilderDescriptionQuestionModal />}
      {measuresEnabledForm &&
      notIsSettings &&
      measuresEnabled &&
      notIsDesign ? (
        <div className="absolute right-0 top-[120px] w-[calc(100%-400px)] border border-solid !border-purple-200 bg-purple-100 py-2.5 text-center font-medium text-purple-600">
          This form is now an assessment and cannot be edited
        </div>
      ) : null}
      <ConditionalRulesModalDialog />
    </>
  )
}

export default FormBuilderLayout
